import React, { useEffect, useMemo, useRef } from 'react';
import { Form } from '@ant-design/compatible';
import '@ant-design/compatible/assets/index.css';
import { Button, Checkbox, Input, Radio } from 'antd';
import { FormComponentProps } from '@ant-design/compatible/es/form';

import { Countries, States, AutoCompleteAddressInput } from '@partsbadger/library';
import type { AddressProps } from '@partsbadger/library';

import countryRegionData, { Country, Region } from 'country-region-data';

type AddressType = 'Billing' | 'Shipping' | 'Billing and Shipping';

interface Props extends FormComponentProps {
  addressType?: AddressType;
  initial?: AddressProps | null;
  handleSave: (value: any) => Promise<any>;
  handleClose: () => void;
  defaultAddressName?: string;
}

const pickCode = (input: any): string =>
  !input ? '' : typeof input === 'string' ? input : (input.key ?? input.code ?? input.value ?? input.short_name ?? input.alpha2 ?? '');

const pickLabel = (input: any, fallback?: string): string =>
  !input ? (fallback || '') : typeof input === 'string' ? input : (input.label ?? input.name ?? input.long_name ?? fallback ?? '');

const sectionPrefix = (t?: AddressType) => (t === 'Billing' ? 'section-billing ' : t === 'Shipping' ? 'section-shipping ' : '');

function normalizeForPayload(values: any, fixedType?: AddressType) {
  const c = values.country?.key ?? values.country?.code ?? values.country ?? '';
  const s = values.state?.key ?? values.state ?? '';

  const type: AddressType = fixedType ? fixedType : (values.type as AddressType) || 'Shipping';
  return {
    name: (values.name || '').trim(),
    type,
    street: (values.street || '').trim(),
    city: (values.city || '').trim(),
    state: s || '',
    country: String(c || '').toUpperCase(),
    zip_code: (values.zip_code || '').trim(),
    primary: !!values.primary,
  };
}

function useCountryLookups() {
  return useMemo(() => {
    const BY_CODE = new Map<string, Country>();
    const BY_NAME = new Map<string, Country>();
    for (const c of countryRegionData) {
      BY_CODE.set((c.countryShortCode || '').toUpperCase(), c);
      BY_NAME.set((c.countryName || '').toLowerCase(), c);
    }
    // common US aliases
    const us = BY_CODE.get('US');
    if (us) {
      BY_NAME.set('united states of america', us);
      BY_NAME.set('usa', us);
      BY_NAME.set('u.s.', us);
      BY_NAME.set('u.s.a.', us);
    }

    const resolveCountry = (raw: string): { code: string; name: string } | null => {
      if (!raw) return null;
      const t = raw.trim();
      // alpha-2 code
      if (/^[A-Za-z]{2}$/.test(t)) {
        const c = BY_CODE.get(t.toUpperCase());
        if (c) return { code: c.countryShortCode.toUpperCase(), name: c.countryName };
      }
      // full name exact
      const nameHit = BY_NAME.get(t.toLowerCase());
      if (nameHit) return { code: nameHit.countryShortCode.toUpperCase(), name: nameHit.countryName };
      // prefix scan (partial name)
      for (const [k, v] of BY_NAME.entries()) {
        if (k.startsWith(t.toLowerCase())) {
          return { code: v.countryShortCode.toUpperCase(), name: v.countryName };
        }
      }
      return null;
    };

    const resolveState = (countryCode: string, input: string): { key: string; label: string } | null => {
      if (!countryCode || !input) return null;
      const c = BY_CODE.get(countryCode.toUpperCase());
      if (!c) return null;
      const q = input.trim().toLowerCase();
      // match by shortCode
      const byCode = c.regions.find((r: Region) => (r.shortCode || '').toLowerCase() === q);
      if (byCode) return { key: byCode.shortCode || byCode.name, label: byCode.name };
      // match by name
      const byName = c.regions.find((r: Region) => (r.name || '').toLowerCase() === q);
      if (byName) return { key: byName.shortCode || byName.name, label: byName.name };
      // prefix name
      const prefix = c.regions.find((r: Region) => (r.name || '').toLowerCase().startsWith(q));
      if (prefix) return { key: prefix.shortCode || prefix.name, label: prefix.name };
      return null;
    };

    return { resolveCountry, resolveState };
  }, []);
}

const NewChromeAddressFormComponent: React.FC<Props> = (props) => {
  const { form, addressType, initial } = props;
  const { getFieldDecorator, getFieldValue, setFieldsValue } = form;
  const autop = sectionPrefix(addressType);

  const { resolveCountry, resolveState } = useCountryLookups();

  // seed initial values
  useEffect(() => {
    const code =
      initial?.country && typeof initial.country === 'object'
        ? (initial.country.code as any)
        : (initial?.country as any);

    const label =
      initial?.country && typeof initial.country === 'object'
        ? initial.country.name || (code as string)
        : (code as string);

    setFieldsValue({
      name:
        initial?.name,
      street: initial?.street,
      city: initial?.city,
      state: initial?.state ? { key: initial.state, label: initial.state } : undefined,
      country: code
        ? { key: String(code).toUpperCase(), label: label || String(code).toUpperCase() }
        : { key: 'US', label: 'United States' },
      zip_code: initial?.zip_code,
      primary: !!initial?.primary,
      type: initial?.type,
    } as any);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initial, addressType]);

  // ZIP validator uses normalized country
  const validateZip = (_rule: any, value: string, cb: (msg?: string) => void) => {
    const c = getFieldValue('country');
    const alpha2 = (pickCode(c) || '').toUpperCase() || 'US';
    if (!value) return cb('Please enter a postal code.');
    if (alpha2 === 'US') {
      if (!/^\d{5}(-\d{4})?$/.test(value)) return cb('Enter a valid US ZIP (12345 or 12345-6789).');
      return cb();
    }
    if (!/^[\p{L}\p{N}\s-]+$/u.test(value)) return cb('Invalid postal code.');
    cb();
  };

  // Handle “Search Address” autofill (from your library)
  const onAutoFill = (addr: { street?: string; city?: string; state?: any; country?: any; zip_code?: string; }) => {
    try {
      const cFromCode = resolveCountry(pickCode(addr.country) || '');
      const cFromLabel = resolveCountry(pickLabel(addr.country) || '');
      const country = (cFromCode ?? cFromLabel) ?? {
        code: (pickCode(addr.country) || 'US').toUpperCase(),
        name: pickLabel(addr.country) || (pickCode(addr.country) || 'US'),
      };

      const st =
        resolveState(country.code, pickCode(addr.state)) ||
        resolveState(country.code, pickLabel(addr.state)) ||
        (pickCode(addr.state)
          ? { key: pickCode(addr.state), label: pickLabel(addr.state, pickCode(addr.state)) }
          : undefined);

      setFieldsValue({
        street: addr.street ?? '',
        city: addr.city ?? '',
        country: { key: country.code, label: country.name },
        state: st,
        zip_code: addr.zip_code ?? '',
      } as any);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('onAutoFill failed:', e);
    }
  };

  // Hidden native inputs (Chrome autofill mirrors) + sync
  const countryHiddenRef = useRef<HTMLInputElement>(null);
  const stateHiddenRef = useRef<HTMLInputElement>(null);

  const syncFromHidden = () => {
    const rawCountry = (countryHiddenRef.current?.value || '').trim();
    const rawState = (stateHiddenRef.current?.value || '').trim();
    if (!rawCountry && !rawState) return;

    const currentCountry = getFieldValue('country');
    const currentState = getFieldValue('state');

    const resolvedCountry =
      resolveCountry(rawCountry) ||
      resolveCountry(pickCode(currentCountry)) ||
      null;

    const nextCountry =
      resolvedCountry
        ? { key: resolvedCountry.code, label: resolvedCountry.name }
        : currentCountry;

    const nextState =
      rawState && resolvedCountry
        ? (resolveState(resolvedCountry.code, rawState) ?? { key: rawState, label: rawState })
        : currentState;

    setFieldsValue({ country: nextCountry, state: nextState } as any);
  };

  useEffect(() => {
    const ch = countryHiddenRef.current;
    const sh = stateHiddenRef.current;
    if (!ch || !sh) return;

    const handler = () => syncFromHidden();
    ch.addEventListener('input', handler);
    ch.addEventListener('change', handler);
    sh.addEventListener('input', handler);
    sh.addEventListener('change', handler);

    // probe after mount to catch silent autofill
    let cancelled = false;
    let ticks = 0;
    const probe = () => {
      if (cancelled || ticks > 12) return;
      ticks += 1;
      syncFromHidden();
      setTimeout(probe, 250);
    };
    setTimeout(probe, 50);

    return () => {
      cancelled = true;
      ch.removeEventListener('input', handler);
      ch.removeEventListener('change', handler);
      sh.removeEventListener('input', handler);
      sh.removeEventListener('change', handler);
    };
  }, []); // once

  const c = getFieldValue('country');
  const alpha2 = (pickCode(c) || '').toUpperCase() || 'US';

  const handleSubmit = () => {
    form.validateFields(async (err, values) => {
      if (err) return;
      const payload = normalizeForPayload(values, addressType);
      try {
        await props.handleSave(payload);
        props.handleClose();
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('Save failed:', e);
      }
    });
  };

  const formItem = { style: { marginBottom: 8, paddingBottom: 0 } };
  const radioStyle: React.CSSProperties = { display: 'block', height: 30, lineHeight: '30px' };

  return (
    <Form layout="vertical" autoComplete="on">
      <Form.Item
        label={addressType ? `${addressType} To: (Full Name or Company)` : 'Full Name or Company'}
        {...formItem}
      >
        {getFieldDecorator('name', {
          rules: [{ required: true, message: 'Please enter a name or company.' }],
        })(
          <Input
            id={`${sectionPrefix(addressType)}name`}
            name="name"
            autoComplete={`${sectionPrefix(addressType)}name`}
            placeholder="e.g., Jane Smith or Acme Inc."
          />
        )}
      </Form.Item>

      {/* hidden mirror for company */}
      <input
        style={{ position: 'absolute', opacity: 0, height: 0, pointerEvents: 'none' }}
        tabIndex={-1}
        aria-hidden
        name="organization"
        autoComplete={`${sectionPrefix(addressType)}organization`}
        value={getFieldValue('name') || ''}
        readOnly
      />

      <Form.Item label="Search Address" {...formItem}>
        <AutoCompleteAddressInput onAutoFill={onAutoFill} />
      </Form.Item>

      <Form.Item label="Street" {...formItem}>
        {getFieldDecorator('street', {
          rules: [{ required: true, message: 'Please enter the street.' }],
        })(
          <Input
            id={`${sectionPrefix(addressType)}address-line1`}
            name="address-line1"
            autoComplete={`${sectionPrefix(addressType)}address-line1`}
            placeholder="Street address"
          />
        )}
      </Form.Item>

      {/* Hidden line2 for Chrome completeness */}
      <input
        style={{ position: 'absolute', opacity: 0, height: 0, pointerEvents: 'none' }}
        tabIndex={-1}
        aria-hidden
        name="address-line2"
        autoComplete={`${sectionPrefix(addressType)}address-line2`}
        defaultValue=""
      />

      <Form.Item label="City" {...formItem}>
        {getFieldDecorator('city', {
          rules: [{ required: true, message: 'Please enter the city.' }],
        })(
          <Input
            id={`${sectionPrefix(addressType)}address-level2`}
            name="address-level2"
            autoComplete={`${sectionPrefix(addressType)}address-level2`}
            placeholder="City"
          />
        )}
      </Form.Item>

      <div className="flex flex-row">
        {/* Country (custom) + hidden native mirror */}
        <div className="w-full md:w-1/2">
          <Form.Item label="Country" {...formItem}>
            {getFieldDecorator('country', {
              rules: [{ required: true, message: 'Please select a country.' }],
              initialValue: { key: 'US', label: 'United States' },
            })(<Countries />)}
          </Form.Item>

          <input
            ref={countryHiddenRef}
            style={{ position: 'absolute', opacity: 0, height: 0, pointerEvents: 'none' }}
            tabIndex={-1}
            aria-hidden
            name="country"
            autoComplete={`${sectionPrefix(addressType)}country`}
            defaultValue=""
          />
        </div>

        {/* State/Region depends on selected country */}
        <div className="w-full md:w-1/2">
          <Form.Item label="State/Region" {...formItem}>
            {getFieldDecorator('state', {
              rules: [{ required: true, message: 'Please select a state/region.' }],
            })(<States countryShortCode={alpha2} />)}
          </Form.Item>

          <input
            ref={stateHiddenRef}
            style={{ position: 'absolute', opacity: 0, height: 0, pointerEvents: 'none' }}
            tabIndex={-1}
            aria-hidden
            name="address-level1"
            autoComplete={`${sectionPrefix(addressType)}address-level1`}
            defaultValue=""
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <Form.Item label="Postal / ZIP" {...formItem}>
          {getFieldDecorator('zip_code', {
            rules: [{ required: true }, { validator: validateZip }],
          })(
            <Input
              id={`${sectionPrefix(addressType)}postal-code`}
              name="postal-code"
              autoComplete={`${sectionPrefix(addressType)}postal-code`}
              placeholder="e.g., 11234"
              inputMode="numeric"
            />
          )}
        </Form.Item>

        {!addressType && (
          <Form.Item label="Type" {...formItem}>
            {getFieldDecorator('type', {
              rules: [{ required: true, message: 'Please choose a type.' }],
            })(
              <Radio.Group size="small">
                <Radio style={{ display: 'block', height: 30, lineHeight: '30px' }} value="Billing">Billing</Radio>
                <Radio style={{ display: 'block', height: 30, lineHeight: '30px' }} value="Shipping">Shipping</Radio>
                <Radio style={{ display: 'block', height: 30, lineHeight: '30px' }} value="Billing and Shipping">Billing and Shipping</Radio>
              </Radio.Group>
            )}
          </Form.Item>
        )}
      </div>

      <Form.Item style={{ marginBottom: 8, paddingBottom: 0 }}>
        {getFieldDecorator('primary', {
          valuePropName: 'checked',
          initialValue: !!initial?.primary,
        })(<Checkbox>Make this my default address</Checkbox>)}
      </Form.Item>

      <div className="flex flex-col p-4 space-y-2" style={{ borderTop: 'solid 1px #d9d9d9' }}>
        <Button type="primary" onClick={handleSubmit}>
          {initial?.id ? 'Update Address' : 'Create New Address'}
        </Button>
        <Button type="link" onClick={props.handleClose}>
          Cancel
        </Button>
      </div>
    </Form>
  );
};

const NewChromeAddressForm = Form.create<Props>()(NewChromeAddressFormComponent);
export default NewChromeAddressForm;
